/** @format */

import type { CustomSeriesRenderItem } from 'echarts'
import type { EChartsOption } from 'echarts'
import { _$ } from '@king-design/react'
import dayjs from 'dayjs'
import type { SessionDetailRootSpan } from './typescript'
import { durationFormatCompact } from 'src/utils/common'
import variable from 'src/static/styles/var'
import { tokenValueFormat } from '../../constant'

export const AXIS_SEGMENTS = 8
export const DATA_ZOOM_HEIGHT = 20
export const DATA_ZOOM_GAP = 8
export const GRID_TOP = 36
export const CHART_TOP = GRID_TOP + DATA_ZOOM_HEIGHT + DATA_ZOOM_GAP
export const ROW_HEIGHT = 28
export const GRID_LEFT = 220
export const TOOLTIP_WIDTH = 320
const NEAR_TIMELINE_END_THRESHOLD_PCT = 15

export interface TimelineRange {
  /** 所有 span 中最早的开始时间（ms） */
  startMs: number
  /** 所有 span 中最晚的结束时间（ms） */
  endMs: number
  totalMs: number
}

function getSpanEndMs(span: SessionDetailRootSpan): number {
  if (span.EndTime) {
    return dayjs(span.EndTime).valueOf()
  }
  return dayjs(span.StartTime).valueOf() + span.DurationMs
}

export function formatAxisTick(offsetMs: number): string {
  if (!Number.isFinite(offsetMs) || offsetMs <= 0) {
    return '0'
  }
  return durationFormatCompact(offsetMs)
}

export interface TimelineBarDatum {
  value: [number, number, number]
  span: SessionDetailRootSpan
  durationLabel: string
  textColor: string
  bgColor: string
  alignRight: boolean
}

function getDurationStyle(durationMs: number) {
  if (durationMs <= 10 * 1000) {
    return { textColor: variable.successColor, bgColor: '#e6ffe6' }
  }
  if (durationMs <= 30 * 1000) {
    return { textColor: variable.warningColor, bgColor: '#fff3e6' }
  }
  return { textColor: variable.errorColor, bgColor: '#ffe6e6' }
}

export function isSpanStartNearTimelineEnd(
  span: SessionDetailRootSpan,
  timelineRange: TimelineRange,
  thresholdPct = NEAR_TIMELINE_END_THRESHOLD_PCT
): boolean {
  const spanStartMs = dayjs(span.StartTime).valueOf()
  const gapMs = timelineRange.endMs - spanStartMs
  return (gapMs / timelineRange.totalMs) * 100 <= thresholdPct
}

/** span 结束时间落在整条时间轴前 thresholdPct% 时，文案靠左 */
export function isSpanEndNearTimelineStart(
  span: SessionDetailRootSpan,
  timelineRange: TimelineRange,
  thresholdPct = NEAR_TIMELINE_END_THRESHOLD_PCT
): boolean {
  const spanEndMs = getSpanEndMs(span)
  const offsetMs = spanEndMs - timelineRange.startMs
  return (offsetMs / timelineRange.totalMs) * 100 <= thresholdPct
}

export function shouldAlignTimelineLabelRight(
  span: SessionDetailRootSpan,
  timelineRange: TimelineRange
): boolean {
  // 终点在时间轴前 15%：靠左；起点在时间轴末 15%：靠右
  if (isSpanEndNearTimelineStart(span, timelineRange)) {
    return false
  }
  return isSpanStartNearTimelineEnd(span, timelineRange)
}

export function formatRowTime(iso?: string): string {
  return iso ? dayjs(iso).format('YYYY-MM-DD HH:mm:ss') : '-'
}

export function getTimelineRange(sorted: SessionDetailRootSpan[]): TimelineRange {
  if (!sorted.length) {
    return { startMs: 0, endMs: 1, totalMs: 1 }
  }
  const startMs = Math.min(...sorted.map(s => dayjs(s.StartTime).valueOf()))
  const endMs = Math.max(...sorted.map(s => getSpanEndMs(s)))
  return { startMs, endMs, totalMs: Math.max(endMs - startMs, 1) }
}

export function buildTimelineBarData(
  sorted: SessionDetailRootSpan[],
  timelineRange: TimelineRange
): TimelineBarDatum[] {
  return sorted.map((span, index) => {
    const spanStartMs = dayjs(span.StartTime).valueOf()
    const spanEndMs = getSpanEndMs(span)
    const startOffsetMs = Math.max(0, spanStartMs - timelineRange.startMs)
    const endOffsetMs = Math.min(timelineRange.totalMs, spanEndMs - timelineRange.startMs)
    const { textColor, bgColor } = getDurationStyle(span.DurationMs)
    return {
      value: [index, startOffsetMs, endOffsetMs],
      span,
      durationLabel: durationFormatCompact(span.DurationMs),
      textColor,
      bgColor,
      alignRight: shouldAlignTimelineLabelRight(span, timelineRange)
    }
  })
}

function tooltipRow(label: string, content: string) {
  return `<div style="display:flex;align-items:flex-start;padding:4px 0;line-height:20px;font-size:12px;">
    <span style="flex-shrink:0;color:${variable.descColor};margin-right:8px;">${label}</span>
    <span style="flex:1;min-width:0;color:rgba(0,0,0,0.88);overflow-wrap:anywhere;word-break:break-all;white-space:normal;">${content}</span>
  </div>`
}

export function buildTimelineTooltipHtml(span: SessionDetailRootSpan): string {
  const hasSpanToken = span.TotalTokens > 0 || span.InputTokens > 0 || span.OutputTokens > 0
  const traceLink = [
    `<a href="javascript:void(0)" class="link-hover-underline" data-timeline-trace`,
    `data-trace-id="${span.TraceId}" data-start-time="${span.StartTime}"`,
    `style="color:${variable.primaryColor};text-decoration:none;overflow-wrap:anywhere;word-break:break-all;">${span.TraceId}</a>`
  ].join(' ')
  return `<div style="width:${TOOLTIP_WIDTH}px;max-width:${TOOLTIP_WIDTH}px;padding-right:8px;box-sizing:border-box;white-space:normal;">
    ${tooltipRow(_$('TraceID'), traceLink)}
    ${tooltipRow(_$('SpanID'), span.SpanId)}
    ${tooltipRow(_$('名称'), span.Name)}
    ${tooltipRow(_$('耗时'), durationFormatCompact(span.DurationMs))}
    ${hasSpanToken ? tooltipRow(_$('Token'), tokenValueFormat(span.TotalTokens)) : ''}
  </div>`
}

const renderTimelineBar =
  (barData: TimelineBarDatum[]): CustomSeriesRenderItem =>
  (params, api) => {
    const dataItem = barData[params.dataIndex]
    if (!dataItem) {
      return
    }

    const categoryIndex = api.value(0) as number
    const startOffsetMs = api.value(1) as number
    const endOffsetMs = api.value(2) as number
    const start = api.coord([startOffsetMs, categoryIndex])
    const end = api.coord([endOffsetMs, categoryIndex])
    if (!start || !end) {
      return
    }

    const bandHeight = api.size([0, 1])[1]
    const barHeight = Math.min(bandHeight * 0.72, 20)
    const width = Math.max(end[0] - start[0], 2)
    const x = start[0]
    const y = start[1] - barHeight / 2

    return {
      type: 'group',
      children: [
        {
          type: 'rect',
          emphasisDisabled: true,
          shape: { x, y, width, height: barHeight, r: 2 },
          style: { fill: dataItem.bgColor },
          styleEmphasis: { fill: dataItem.bgColor }
        },
        {
          type: 'text',
          emphasisDisabled: true,
          style: {
            text: dataItem.durationLabel,
            fill: dataItem.textColor,
            fontSize: 12,
            x: dataItem.alignRight ? x + width - 4 : x + 4,
            y: y + barHeight / 2,
            textAlign: dataItem.alignRight ? 'right' : 'left',
            textVerticalAlign: 'middle'
          }
        }
      ]
    }
  }

export function buildTimelineChartOption(
  sorted: SessionDetailRootSpan[],
  timelineRange: TimelineRange,
  barData: TimelineBarDatum[]
): EChartsOption {
  const categories = sorted.map((_, index) => String(index))
  const axisInterval = timelineRange.totalMs / AXIS_SEGMENTS

  return {
    animation: false,
    grid: {
      left: GRID_LEFT,
      right: 12,
      top: CHART_TOP,
      bottom: 8,
      containLabel: false
    },
    dataZoom: [
      {
        type: 'slider',
        xAxisIndex: 0,
        top: 4,
        height: DATA_ZOOM_HEIGHT,
        right: 12,
        showDetail: false,
        brushSelect: false
      },
      {
        type: 'inside',
        xAxisIndex: 0,
        filterMode: 'none',
        moveOnMouseMove: true,
        zoomOnMouseWheel: true
      }
    ],
    tooltip: {
      trigger: 'item',
      enterable: true,
      confine: true,
      borderWidth: 0,
      padding: 12,
      extraCssText:
        'max-width:320px;white-space:normal;word-break:break-all;border-radius:4px;box-shadow:0 6px 16px 0 rgba(0,0,0,0.08),0 3px 6px -4px rgba(0,0,0,0.12),0 9px 28px 8px rgba(0,0,0,0.05);',
      position: (point, params, _dom, _rect, size) => {
        const raw = Array.isArray(params) ? params[0] : params
        const data =
          (raw?.data as TimelineBarDatum | undefined) ?? barData[raw?.dataIndex as number]
        const alignRight = data?.alignRight ?? false
        const [mouseX, mouseY] = point
        const { contentSize, viewSize } = size
        const offset = 12
        let x = alignRight ? mouseX - contentSize[0] - offset : mouseX + offset
        let y = mouseY + offset
        if (alignRight && x < 8) {
          x = mouseX + offset
        } else if (!alignRight && x + contentSize[0] > viewSize[0] - 8) {
          x = mouseX - contentSize[0] - offset
        }
        if (y + contentSize[1] > viewSize[1] - 8) {
          y = mouseY - contentSize[1] - offset
        }
        return [Math.max(8, Math.min(x, viewSize[0] - contentSize[0] - 8)), Math.max(8, y)]
      },
      formatter: params => {
        const raw = Array.isArray(params) ? params[0] : params
        const item =
          (raw?.data as TimelineBarDatum | undefined) ?? barData[raw?.dataIndex as number]
        return item ? buildTimelineTooltipHtml(item.span) : ''
      }
    },
    xAxis: {
      type: 'value',
      position: 'top',
      min: 0,
      max: timelineRange.totalMs,
      interval: axisInterval,
      axisLine: {
        show: true,
        onZero: false,
        lineStyle: { color: 'rgba(0, 0, 0, 0.08)' }
      },
      axisTick: {
        show: true,
        length: 4,
        lineStyle: { color: 'rgba(0, 0, 0, 0.15)' }
      },
      axisLabel: {
        color: 'rgba(0, 0, 0, 0.45)',
        fontSize: 12,
        margin: 8,
        showMinLabel: true,
        showMaxLabel: true,
        formatter: (value: number) => formatAxisTick(value)
      },
      splitLine: { show: false }
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: categories,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        width: 210,
        overflow: 'truncate',
        margin: 16,
        formatter: (value: string) => {
          const index = Number(value)
          const span = sorted[index]
          if (!span) {
            return ''
          }
          return `{call|${_$(`第{x}条调用链`, { x: index + 1 })}}  {time|${formatRowTime(
            span.StartTime
          )}}`
        },
        rich: {
          call: {
            color: variable.descColor,
            fontSize: 12,
            lineHeight: 20
          },
          time: {
            color: 'rgba(0, 0, 0, 0.88)',
            fontSize: 12,
            lineHeight: 20,
            fontFamily: 'tabular-nums'
          }
        }
      }
    },
    series: [
      {
        type: 'custom',
        renderItem: renderTimelineBar(barData),
        encode: {
          x: [1, 2],
          y: 0
        },
        data: barData,
        cursor: 'pointer'
      }
    ]
  }
}

export function getTimelineChartHeight(rowCount: number): number {
  return Math.max(CHART_TOP + 8 + rowCount * ROW_HEIGHT, 80)
}
